export enum AppointmentStatus {
  SCHEDULED = 'SCHEDULED',
  CONFIRMED = 'CONFIRMED',
  ARRIVED = 'ARRIVED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  NO_SHOW = 'NO_SHOW',
  RESCHEDULED = 'RESCHEDULED',
}

export type AppointmentStatusType = AppointmentStatus;

export type AppointmentType = 'CONSULTATION' | 'FOLLOW_UP' | 'THERAPIES' | 'OTC' | 'WALK_IN' | 'OTHER';

export interface PatientDto {
  id: string;
  mrn: string;
  fullName: string;
  age: number;
  gender: string;
  phone: string;
  email: string;
  address: string;
  note?: string;
  isActive: boolean;
}

export interface DepartmentDto {
  id: string;
  name: string;
}

export interface EmployeeDto {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  fullName?: string; // Computed property for display
  email?: string;
  phone?: string;
  designation?: string;
  employeeType: string;
  qualification?: string;
  experience?: number;
  status?: string;
  department?: DepartmentDto;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface LocationDto {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  isActive: boolean;
  code?: string;
  locationType?: string;
  description?: string;
}

export interface AppointmentDto {
  id: string;
  appointmentNumber: string;
  appointmentDate: string;
  patientId: string;
  doctorId: string;
  locationId: string;
  startTime: string;
  endTime: string;
  type: AppointmentType;
  status: AppointmentStatus;
  reason?: string;
  notes?: string;
  isWalkIn: boolean;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  patient: PatientDto;
  doctor: EmployeeDto;
  location: LocationDto;
  
  // Consultation fields
  chiefComplaint?: string;
  diagnosis?: string;
  historyOfPresentIllness?: string;
  consultationDate?: string;
  healthQuestionnaire?: {
    conditions: string[];
    additionalNotes: string;
  };
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface Appointment extends Omit<AppointmentDto, 'patient' | 'doctor' | 'location' | 'priority'> {
  appointmentNumber: string;
  appointmentDate: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  startTime: string;
  endTime: string;
  patientName: string;
  doctorName: string;
  patient?: PatientDto;
  doctor?: EmployeeDto;
  location?: LocationDto;
  date?: string;
  time?: string;
  status: AppointmentStatus;
  
  // Consultation fields
  chiefComplaint?: string;
  diagnosis?: string;
  historyOfPresentIllness?: string;
  notes?: string;
  consultationDate?: string;
  healthQuestionnaire?: {
    conditions: string[];
    additionalNotes: string;
  };
}

export interface AppointmentFormData {
  patientId: string;
  doctorId: string;
  locationId: string;
  date: string;
  time: string;
  endTime?: string;  // Added endTime property
  duration: number;  // in minutes
  type: AppointmentType;
  status: AppointmentStatus;
  reason?: string;
  notes?: string;
  isWalkIn: boolean;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
}

export interface GetAppointmentsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: AppointmentStatusType[];
  type?: AppointmentType[];
  patientId?: string;
  doctorId?: string;
  locationId?: string;
  startDate?: string;
  endDate?: string;
  isWalkIn?: boolean;
  priority?: ('LOW' | 'MEDIUM' | 'HIGH' | 'URGENT')[];
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type AppointmentFilter = GetAppointmentsQueryParams;
